import { useCallback, useState } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { Channel, ChannelItem, SyndicateItemParams } from 'types/channel';
import {
  getChannelItems,
  listChannels,
  syndicateItem,
} from '../api/marketplaceApi';

export type SyndicationCandidateChannel = Channel & {
  is_subscribed?: boolean;
  is_mandatory?: boolean;
  item_count?: number;
  subscriber_count?: number;
};

export interface UseSyndicateItemResult {
  channels: SyndicationCandidateChannel[];
  items: ChannelItem[];
  selectedChannel: SyndicationCandidateChannel | null;
  selectedItem: ChannelItem | null;
  loading: boolean;
  error: string | null;
  searchChannels: (query?: string) => Promise<void>;
  selectChannel: (channel: SyndicationCandidateChannel | null) => Promise<void>;
  selectItem: (item: ChannelItem | null) => void;
  syndicate: (params: SyndicateItemParams) => Promise<ChannelItem | null>;
  reset: () => void;
}

function matchesQuery(channel: Channel, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;

  const defaultName = channel.name?.default?.toLowerCase() ?? '';
  if (defaultName.includes(normalized)) return true;

  const translations = channel.name?.translations ?? {};
  return Object.values(translations).some((value) =>
    value.toLowerCase().includes(normalized)
  );
}

const CHANNEL_PAGE_SIZE = 50;
const ITEM_PAGE_SIZE = 100;

export function useSyndicateItem(
  targetChannelId: string | null
): UseSyndicateItemResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [channels, setChannels] = useState<SyndicationCandidateChannel[]>([]);
  const [items, setItems] = useState<ChannelItem[]>([]);
  const [selectedChannel, setSelectedChannel] =
    useState<SyndicationCandidateChannel | null>(null);
  const [selectedItem, setSelectedItem] = useState<ChannelItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchChannels = useCallback(
    async (query?: string): Promise<void> => {
      if (!apiClients || !organizationId) {
        setChannels([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await listChannels(apiClients.schemaClient, {
          limit: CHANNEL_PAGE_SIZE,
        });

        const filtered = response.channels.filter((channel) => {
          if (targetChannelId && channel.id === targetChannelId) return false;
          return matchesQuery(channel, query ?? '');
        });

        setChannels(filtered);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load channels';
        setError(message);
        setChannels([]);
      } finally {
        setLoading(false);
      }
    },
    [apiClients, organizationId, targetChannelId]
  );

  const selectChannel = useCallback(
    async (channel: SyndicationCandidateChannel | null): Promise<void> => {
      setSelectedChannel(channel);
      setSelectedItem(null);
      setItems([]);

      if (!channel || !apiClients || !organizationId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await getChannelItems(
          apiClients.schemaClient,
          channel.id,
          { limit: ITEM_PAGE_SIZE }
        );

        const shareable = response.data.filter((item) => !item.is_syndicated);
        setItems(shareable);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load items';
        setError(message);
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [apiClients, organizationId]
  );

  const selectItem = useCallback((item: ChannelItem | null): void => {
    setSelectedItem(item);
  }, []);

  const syndicate = useCallback(
    async (params: SyndicateItemParams): Promise<ChannelItem | null> => {
      if (!apiClients || !targetChannelId) return null;

      setLoading(true);
      setError(null);

      try {
        const response = await syndicateItem(
          apiClients.schemaClient,
          targetChannelId,
          params
        );
        return response.channelItem;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to syndicate item';
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [apiClients, targetChannelId]
  );

  const reset = useCallback((): void => {
    setChannels([]);
    setItems([]);
    setSelectedChannel(null);
    setSelectedItem(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    channels,
    items,
    selectedChannel,
    selectedItem,
    loading,
    error,
    searchChannels,
    selectChannel,
    selectItem,
    syndicate,
    reset,
  };
}
